<template>
  <section id="leaderboard" class="relative py-16 md:py-24 bg-gradient-to-br from-blue-50 via-white to-amber-50">
    <!-- Decorative background elements -->
    <div class="absolute inset-0 overflow-hidden pointer-events-none">
      <div class="absolute -top-24 -right-24 w-96 h-96 bg-gradient-to-br from-amber-100/30 to-orange-100/30 rounded-full blur-3xl"></div>
      <div class="absolute -bottom-32 -left-32 w-80 h-80 bg-gradient-to-br from-blue-100/30 to-purple-100/30 rounded-full blur-3xl"></div>
    </div>

    <div class="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <!-- Header -->
      <div class="text-center mb-16">
        <div class="inline-flex items-center gap-3 mb-6">
          <div class="relative">
            <div class="w-20 h-20 bg-gradient-to-r from-yellow-400 to-orange-500 rounded-2xl flex items-center justify-center shadow-xl animate-float-subtle">
              <v-icon color="white" size="40">mdi-trophy</v-icon>
            </div>
            <div class="absolute -top-2 -right-2 w-8 h-8 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full flex items-center justify-center animate-pulse">
              <v-icon color="white" size="16">mdi-star</v-icon>
            </div>
          </div>
        </div>
        
        <h2 class="text-5xl md:text-6xl font-black mb-6 leading-tight bg-gradient-to-r from-yellow-500 to-amber-600 bg-clip-text text-transparent">
          Leaderboard
        </h2>
        <p class="text-xl text-gray-600 max-w-3xl mx-auto mb-8 leading-relaxed">
          Celebrating the legendary duck hunters and master hiders who rule the roost! 
          <span class="text-amber-600 font-semibold">Join their ranks today.</span>
        </p>
        
        <!-- Scoring Legend -->
        <div class="inline-flex flex-wrap items-center justify-center gap-6 text-sm bg-white/80 backdrop-blur-sm rounded-2xl p-6 shadow-lg border border-white/20 mb-4">
          <div class="flex items-center gap-2">
            <div class="w-8 h-8 bg-gradient-to-r from-yellow-400 to-amber-500 rounded-full flex items-center justify-center shadow-sm">
              <v-icon size="16" color="white">mdi-diamond-stone</v-icon>
            </div>
            <span class="text-gray-700 font-semibold">Legendary <span class="text-yellow-600">3pts</span></span>
          </div>
          <div class="flex items-center gap-2">
            <div class="w-8 h-8 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full flex items-center justify-center shadow-sm">
              <v-icon size="16" color="white">mdi-star</v-icon>
            </div>
            <span class="text-gray-700 font-semibold">Rare <span class="text-purple-600">2pts</span></span>
          </div>
          <div class="flex items-center gap-2">
            <div class="w-8 h-8 bg-gradient-to-r from-blue-500 to-cyan-500 rounded-full flex items-center justify-center shadow-sm">
              <v-icon size="16" color="white">mdi-circle</v-icon>
            </div>
            <span class="text-gray-700 font-semibold">Common <span class="text-blue-600">1pt</span></span>
          </div>
        </div>
        
        <!-- Explanation text -->
        <div class="text-center mb-8">
          <p class="text-sm text-gray-600 bg-blue-50/80 rounded-xl px-4 py-2 inline-block">
            <v-icon size="16" class="mr-2 text-blue-600">mdi-information</v-icon>
            Numbers shown below each player represent how many ducks of each rarity they've found or hidden
          </p>
        </div>
        
        <!-- Expand/Collapse Button -->
        <div class="mt-6">
          <button 
            @click="toggleExpanded"
            class="group inline-flex items-center px-8 py-4 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white rounded-2xl font-bold text-lg shadow-xl hover:shadow-2xl transition-all duration-300 transform hover:scale-105 hover:-translate-y-1"
            :aria-expanded="isExpanded"
            aria-controls="leaderboard-content"
          >
            <v-icon size="24" class="mr-3 transition-transform group-hover:scale-110">{{ isExpanded ? 'mdi-chevron-up' : 'mdi-view-list' }}</v-icon>
            {{ isExpanded ? 'Show Less' : 'View Full Leaderboard' }}
            <div class="absolute inset-0 rounded-2xl bg-white/20 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
          </button>
        </div>
      </div>

      <!-- Loading State -->
      <div v-if="loading" class="flex justify-center items-center py-20">
        <div class="relative">
          <div class="w-16 h-16 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin"></div>
          <div class="absolute inset-0 flex items-center justify-center">
            <v-icon color="blue" size="20">mdi-duck</v-icon>
          </div>
        </div>
      </div>

      <!-- Error State -->
      <div v-else-if="error" class="text-center py-20">
        <div class="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
          <v-icon color="error" size="32">mdi-alert-circle</v-icon>
        </div>
        <h3 class="text-xl font-bold text-gray-800 mb-2">Oops! Something went wrong</h3>
        <p class="text-gray-600 mb-6">Failed to load leaderboard data. Please try again later.</p>
        <button 
          @click="fetchLeaderboard"
          class="inline-flex items-center px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-medium transition-colors"
        >
          <v-icon size="20" class="mr-2">mdi-refresh</v-icon>
          Try Again
        </button>
      </div>

      <!-- Leaderboard Content -->
  <div v-else-if="leaderboard" id="leaderboard-content" class="space-y-8">
        <!-- Champions Podium (Top 3) -->
        <div v-if="!isExpanded" class="grid grid-cols-1 lg:grid-cols-2 gap-8">
          <!-- Top 3 Finders Podium -->
          <div class="relative">
            <div class="text-center mb-8">
              <div class="inline-flex items-center gap-3 mb-4">
                <div class="w-12 h-12 bg-gradient-to-r from-emerald-500 to-green-600 rounded-xl flex items-center justify-center shadow-lg">
                  <v-icon color="white" size="24">mdi-duck</v-icon>
                </div>
                <h3 class="text-2xl font-bold text-gray-800">Top Duck Finders</h3>
              </div>
              <p class="text-gray-600">Champions of Discovery</p>
            </div>

            <div class="relative flex flex-col sm:flex-row items-stretch sm:items-end justify-center gap-4 mb-6">
              <!-- 2nd Place -->
              <div v-if="displayTopFinders[1]" class="flex flex-col items-center order-2 sm:order-1 w-full sm:w-auto">
                <div class="relative mb-4">
                  <div class="w-16 h-16 bg-gradient-to-r from-gray-300 to-gray-500 rounded-full flex items-center justify-center shadow-lg">
                    <span class="text-xl font-black text-white">2</span>
                  </div>
                  <div class="absolute -top-2 -right-2 w-6 h-6 bg-silver rounded-full"></div>
                </div>
                <div class="bg-white/80 backdrop-blur-sm rounded-2xl p-4 shadow-lg border border-gray-100 min-h-[120px] w-full sm:w-32">
                  <p class="font-bold text-gray-800 text-sm text-center truncate">{{ displayTopFinders[1].username }}</p>
                  <div class="text-center mt-2">
                    <p class="text-2xl font-black text-emerald-600">{{ calculateFoundScore(displayTopFinders[1]) }}</p>
                    <p class="text-xs text-gray-500 uppercase tracking-wide">points</p>
                  </div>
                  <div class="flex justify-center gap-2 mt-2 text-xs">
                    <div class="flex items-center gap-1 text-yellow-600">
                      <v-icon size="12">mdi-diamond-stone</v-icon>
                      <span class="font-semibold">{{ displayTopFinders[1].ducksFoundByRarity?.LEGENDARY || 0 }}</span>
                    </div>
                    <div class="flex items-center gap-1 text-purple-600">
                      <v-icon size="12">mdi-star</v-icon>
                      <span class="font-semibold">{{ displayTopFinders[1].ducksFoundByRarity?.RARE || 0 }}</span>
                    </div>
                    <div class="flex items-center gap-1 text-blue-600">
                      <v-icon size="12">mdi-circle</v-icon>
                      <span class="font-semibold">{{ displayTopFinders[1].ducksFoundByRarity?.COMMON || 0 }}</span>
                    </div>
                  </div>
                </div>
              </div>

              <!-- 1st Place -->
              <div v-if="displayTopFinders[0]" class="flex flex-col items-center relative z-10 order-1 sm:order-2 w-full sm:w-auto">
                <div class="relative mb-4">
                  <div class="w-20 h-20 bg-gradient-to-r from-yellow-400 to-amber-500 rounded-full flex items-center justify-center shadow-xl animate-pulse">
                    <span class="text-2xl font-black text-white">1</span>
                  </div>
                  <div class="absolute -top-3 -right-3 w-8 h-8 bg-gradient-to-r from-yellow-400 to-orange-500 rounded-full flex items-center justify-center">
                    <v-icon color="white" size="16">mdi-crown</v-icon>
                  </div>
                </div>
                <div class="bg-gradient-to-br from-yellow-50 to-amber-50 rounded-2xl p-6 shadow-xl border-2 border-yellow-200 min-h-[140px] w-full sm:w-36">
                  <p class="font-black text-gray-800 text-center truncate">{{ displayTopFinders[0].username }}</p>
                  <div class="text-center mt-3">
                    <p class="text-3xl font-black text-yellow-600">{{ calculateFoundScore(displayTopFinders[0]) }}</p>
                    <p class="text-xs text-gray-600 uppercase tracking-wide font-semibold">champion</p>
                  </div>
                  <div class="flex justify-center gap-2 mt-3 text-sm">
                    <div class="flex items-center gap-1 text-yellow-700">
                      <v-icon size="14">mdi-diamond-stone</v-icon>
                      <span class="font-bold">{{ displayTopFinders[0].ducksFoundByRarity?.LEGENDARY || 0 }}</span>
                    </div>
                    <div class="flex items-center gap-1 text-purple-700">
                      <v-icon size="14">mdi-star</v-icon>
                      <span class="font-bold">{{ displayTopFinders[0].ducksFoundByRarity?.RARE || 0 }}</span>
                    </div>
                    <div class="flex items-center gap-1 text-blue-700">
                      <v-icon size="14">mdi-circle</v-icon>
                      <span class="font-bold">{{ displayTopFinders[0].ducksFoundByRarity?.COMMON || 0 }}</span>
                    </div>
                  </div>
                </div>
              </div>

              <!-- 3rd Place -->
              <div v-if="displayTopFinders[2]" class="flex flex-col items-center order-3 sm:order-3 w-full sm:w-auto">
                <div class="relative mb-4">
                  <div class="w-16 h-16 bg-gradient-to-r from-amber-600 to-orange-700 rounded-full flex items-center justify-center shadow-lg">
                    <span class="text-xl font-black text-white">3</span>
                  </div>
                </div>
                <div class="bg-white/80 backdrop-blur-sm rounded-2xl p-4 shadow-lg border border-gray-100 min-h-[120px] w-full sm:w-32">
                  <p class="font-bold text-gray-800 text-sm text-center truncate">{{ displayTopFinders[2].username }}</p>
                  <div class="text-center mt-2">
                    <p class="text-2xl font-black text-emerald-600">{{ calculateFoundScore(displayTopFinders[2]) }}</p>
                    <p class="text-xs text-gray-500 uppercase tracking-wide">points</p>
                  </div>
                  <div class="flex justify-center gap-2 mt-2 text-xs">
                    <div class="flex items-center gap-1 text-yellow-600">
                      <v-icon size="12">mdi-diamond-stone</v-icon>
                      <span class="font-semibold">{{ displayTopFinders[2].ducksFoundByRarity?.LEGENDARY || 0 }}</span>
                    </div>
                    <div class="flex items-center gap-1 text-purple-600">
                      <v-icon size="12">mdi-star</v-icon>
                      <span class="font-semibold">{{ displayTopFinders[2].ducksFoundByRarity?.RARE || 0 }}</span>
                    </div>
                    <div class="flex items-center gap-1 text-blue-600">
                      <v-icon size="12">mdi-circle</v-icon>
                      <span class="font-semibold">{{ displayTopFinders[2].ducksFoundByRarity?.COMMON || 0 }}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Empty state for finders -->
            <div v-if="displayTopFinders.length === 0" class="text-center py-12">
              <div class="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <v-icon size="32" color="gray">mdi-duck</v-icon>
              </div>
              <p class="text-gray-500 font-medium">No duck finders yet. Be the first!</p>
            </div>
          </div>

          <!-- Top 3 Hiders Podium -->
          <div class="relative">
            <div class="text-center mb-8">
              <div class="inline-flex items-center gap-3 mb-4">
                <div class="w-12 h-12 bg-gradient-to-r from-orange-500 to-red-600 rounded-xl flex items-center justify-center shadow-lg">
                  <v-icon color="white" size="24">mdi-eye-off</v-icon>
                </div>
                <h3 class="text-2xl font-bold text-gray-800">Top Duck Hiders</h3>
              </div>
              <p class="text-gray-600">Masters of Concealment</p>
            </div>

            <div class="relative flex flex-col sm:flex-row items-stretch sm:items-end justify-center gap-4 mb-6">
              <!-- 2nd Place -->
              <div v-if="displayTopHiders[1]" class="flex flex-col items-center order-2 sm:order-1 w-full sm:w-auto">
                <div class="relative mb-4">
                  <div class="w-16 h-16 bg-gradient-to-r from-gray-300 to-gray-500 rounded-full flex items-center justify-center shadow-lg">
                    <span class="text-xl font-black text-white">2</span>
                  </div>
                  <div class="absolute -top-2 -right-2 w-6 h-6 bg-silver rounded-full"></div>
                </div>
                <div class="bg-white/80 backdrop-blur-sm rounded-2xl p-4 shadow-lg border border-gray-100 min-h-[120px] w-full sm:w-32">
                  <p class="font-bold text-gray-800 text-sm text-center truncate">{{ displayTopHiders[1].username }}</p>
                  <div class="text-center mt-2">
                    <p class="text-2xl font-black text-orange-600">{{ calculateHiddenScore(displayTopHiders[1]) }}</p>
                    <p class="text-xs text-gray-500 uppercase tracking-wide">points</p>
                  </div>
                  <div class="flex justify-center gap-2 mt-2 text-xs">
                    <div class="flex items-center gap-1 text-yellow-600">
                      <v-icon size="12">mdi-diamond-stone</v-icon>
                      <span class="font-semibold">{{ displayTopHiders[1].ducksHiddenByRarity?.LEGENDARY || 0 }}</span>
                    </div>
                    <div class="flex items-center gap-1 text-purple-600">
                      <v-icon size="12">mdi-star</v-icon>
                      <span class="font-semibold">{{ displayTopHiders[1].ducksHiddenByRarity?.RARE || 0 }}</span>
                    </div>
                    <div class="flex items-center gap-1 text-blue-600">
                      <v-icon size="12">mdi-circle</v-icon>
                      <span class="font-semibold">{{ displayTopHiders[1].ducksHiddenByRarity?.COMMON || 0 }}</span>
                    </div>
                  </div>
                </div>
              </div>

              <!-- 1st Place -->
              <div v-if="displayTopHiders[0]" class="flex flex-col items-center relative z-10 order-1 sm:order-2 w-full sm:w-auto">
                <div class="relative mb-4">
                  <div class="w-20 h-20 bg-gradient-to-r from-yellow-400 to-amber-500 rounded-full flex items-center justify-center shadow-xl animate-pulse">
                    <span class="text-2xl font-black text-white">1</span>
                  </div>
                  <div class="absolute -top-3 -right-3 w-8 h-8 bg-gradient-to-r from-yellow-400 to-orange-500 rounded-full flex items-center justify-center">
                    <v-icon color="white" size="16">mdi-crown</v-icon>
                  </div>
                </div>
                <div class="bg-gradient-to-br from-yellow-50 to-amber-50 rounded-2xl p-6 shadow-xl border-2 border-yellow-200 min-h-[140px] w-full sm:w-36">
                  <p class="font-black text-gray-800 text-center truncate">{{ displayTopHiders[0].username }}</p>
                  <div class="text-center mt-3">
                    <p class="text-3xl font-black text-yellow-600">{{ calculateHiddenScore(displayTopHiders[0]) }}</p>
                    <p class="text-xs text-gray-600 uppercase tracking-wide font-semibold">champion</p>
                  </div>
                  <div class="flex justify-center gap-2 mt-3 text-sm">
                    <div class="flex items-center gap-1 text-yellow-700">
                      <v-icon size="14">mdi-diamond-stone</v-icon>
                      <span class="font-bold">{{ displayTopHiders[0].ducksHiddenByRarity?.LEGENDARY || 0 }}</span>
                    </div>
                    <div class="flex items-center gap-1 text-purple-700">
                      <v-icon size="14">mdi-star</v-icon>
                      <span class="font-bold">{{ displayTopHiders[0].ducksHiddenByRarity?.RARE || 0 }}</span>
                    </div>
                    <div class="flex items-center gap-1 text-blue-700">
                      <v-icon size="14">mdi-circle</v-icon>
                      <span class="font-bold">{{ displayTopHiders[0].ducksHiddenByRarity?.COMMON || 0 }}</span>
                    </div>
                  </div>
                </div>
              </div>

              <!-- 3rd Place -->
              <div v-if="displayTopHiders[2]" class="flex flex-col items-center order-3 sm:order-3 w-full sm:w-auto">
                <div class="relative mb-4">
                  <div class="w-16 h-16 bg-gradient-to-r from-amber-600 to-orange-700 rounded-full flex items-center justify-center shadow-lg">
                    <span class="text-xl font-black text-white">3</span>
                  </div>
                </div>
                <div class="bg-white/80 backdrop-blur-sm rounded-2xl p-4 shadow-lg border border-gray-100 min-h-[120px] w-full sm:w-32">
                  <p class="font-bold text-gray-800 text-sm text-center truncate">{{ displayTopHiders[2].username }}</p>
                  <div class="text-center mt-2">
                    <p class="text-2xl font-black text-orange-600">{{ calculateHiddenScore(displayTopHiders[2]) }}</p>
                    <p class="text-xs text-gray-500 uppercase tracking-wide">points</p>
                  </div>
                  <div class="flex justify-center gap-2 mt-2 text-xs">
                    <div class="flex items-center gap-1 text-yellow-600">
                      <v-icon size="12">mdi-diamond-stone</v-icon>
                      <span class="font-semibold">{{ displayTopHiders[2].ducksHiddenByRarity?.LEGENDARY || 0 }}</span>
                    </div>
                    <div class="flex items-center gap-1 text-purple-600">
                      <v-icon size="12">mdi-star</v-icon>
                      <span class="font-semibold">{{ displayTopHiders[2].ducksHiddenByRarity?.RARE || 0 }}</span>
                    </div>
                    <div class="flex items-center gap-1 text-blue-600">
                      <v-icon size="12">mdi-circle</v-icon>
                      <span class="font-semibold">{{ displayTopHiders[2].ducksHiddenByRarity?.COMMON || 0 }}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Empty state for hiders -->
            <div v-if="displayTopHiders.length === 0" class="text-center py-12">
              <div class="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <v-icon size="32" color="gray">mdi-eye-off</v-icon>
              </div>
              <p class="text-gray-500 font-medium">No duck hiders yet. Be the first!</p>
            </div>
          </div>
        </div>
        <!-- Full Expanded List View -->
        <div v-else class="grid grid-cols-1 lg:grid-cols-2 gap-8">
          <!-- All Finders -->
          <div class="bg-white/90 backdrop-blur-sm rounded-3xl shadow-xl border border-gray-100 overflow-hidden">
            <div class="bg-gradient-to-r from-emerald-500 to-green-600 p-6 text-white">
              <div class="flex items-center gap-4">
                <div class="w-16 h-16 bg-white/20 rounded-2xl flex items-center justify-center">
                  <v-icon color="white" size="32">mdi-duck</v-icon>
                </div>
                <div>
                  <h3 class="text-3xl font-black">Duck Finders</h3>
                  <p class="text-emerald-100">Complete Rankings</p>
                </div>
              </div>
            </div>

            <div class="divide-y divide-gray-100">
              <div
                v-for="(entry, index) in displayTopFinders"
                :key="`full-finder-${entry.username}`"
                class="p-6 hover:bg-gray-50 transition-colors group"
              >
                <div class="flex items-center gap-4">
                  <!-- Rank Badge -->
                  <div
                    class="relative w-14 h-14 rounded-2xl flex items-center justify-center text-lg font-black shadow-lg transition-transform group-hover:scale-110"
                    :class="[
                      index === 0 ? 'bg-gradient-to-r from-yellow-400 to-amber-500 text-white' :
                      index === 1 ? 'bg-gradient-to-r from-gray-400 to-gray-600 text-white' :
                      index === 2 ? 'bg-gradient-to-r from-amber-600 to-orange-700 text-white' :
                      'bg-gradient-to-r from-blue-500 to-cyan-600 text-white'
                    ]"
                  >
                    {{ index + 1 }}
                    <div v-if="index < 3" class="absolute -top-2 -right-2 w-6 h-6 bg-yellow-400 rounded-full flex items-center justify-center">
                      <v-icon color="yellow-darken-3" size="12">mdi-star</v-icon>
                    </div>
                  </div>

                  <!-- Player Info -->
                  <div class="flex-1 min-w-0">
                    <h4 class="text-xl font-bold text-gray-800 truncate">{{ entry.username }}</h4>
                    <div class="flex items-center gap-4 mt-2">
                      <div class="flex items-center gap-2">
                        <div class="w-6 h-6 bg-yellow-100 rounded-full flex items-center justify-center">
                          <v-icon color="yellow-darken-2" size="12">mdi-diamond-stone</v-icon>
                        </div>
                        <span class="text-sm font-bold text-yellow-700">{{ entry.ducksFoundByRarity?.LEGENDARY || 0 }}</span>
                      </div>
                      <div class="flex items-center gap-2">
                        <div class="w-6 h-6 bg-purple-100 rounded-full flex items-center justify-center">
                          <v-icon color="purple-darken-2" size="12">mdi-star</v-icon>
                        </div>
                        <span class="text-sm font-bold text-purple-700">{{ entry.ducksFoundByRarity?.RARE || 0 }}</span>
                      </div>
                      <div class="flex items-center gap-2">
                        <div class="w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center">
                          <v-icon color="blue-darken-2" size="12">mdi-circle</v-icon>
                        </div>
                        <span class="text-sm font-bold text-blue-700">{{ entry.ducksFoundByRarity?.COMMON || 0 }}</span>
                      </div>
                    </div>
                  </div>

                  <!-- Score -->
                  <div class="text-right">
                    <div class="text-3xl font-black text-emerald-600">{{ calculateFoundScore(entry) }}</div>
                    <div class="text-xs text-gray-500 uppercase tracking-wide font-semibold">total points</div>
                  </div>
                </div>
              </div>

              <div v-if="displayTopFinders.length === 0" class="py-16 text-center">
                <div class="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <v-icon size="40" color="gray">mdi-duck</v-icon>
                </div>
                <h4 class="text-xl font-bold text-gray-800 mb-2">No Finders Yet</h4>
                <p class="text-gray-600">Be the first to find a hidden duck!</p>
              </div>
            </div>
          </div>

          <!-- All Hiders -->
          <div class="bg-white/90 backdrop-blur-sm rounded-3xl shadow-xl border border-gray-100 overflow-hidden">
            <div class="bg-gradient-to-r from-orange-500 to-red-600 p-6 text-white">
              <div class="flex items-center gap-4">
                <div class="w-16 h-16 bg-white/20 rounded-2xl flex items-center justify-center">
                  <v-icon color="white" size="32">mdi-eye-off</v-icon>
                </div>
                <div>
                  <h3 class="text-3xl font-black">Duck Hiders</h3>
                  <p class="text-orange-100">Complete Rankings</p>
                </div>
              </div>
            </div>

            <div class="divide-y divide-gray-100">
              <div
                v-for="(entry, index) in displayTopHiders"
                :key="`full-hider-${entry.username}`"
                class="p-6 hover:bg-gray-50 transition-colors group"
              >
                <div class="flex items-center gap-4">
                  <!-- Rank Badge -->
                  <div
                    class="relative w-14 h-14 rounded-2xl flex items-center justify-center text-lg font-black shadow-lg transition-transform group-hover:scale-110"
                    :class="[
                      index === 0 ? 'bg-gradient-to-r from-yellow-400 to-amber-500 text-white' :
                      index === 1 ? 'bg-gradient-to-r from-gray-400 to-gray-600 text-white' :
                      index === 2 ? 'bg-gradient-to-r from-amber-600 to-orange-700 text-white' :
                      'bg-gradient-to-r from-blue-500 to-cyan-600 text-white'
                    ]"
                  >
                    {{ index + 1 }}
                    <div v-if="index < 3" class="absolute -top-2 -right-2 w-6 h-6 bg-yellow-400 rounded-full flex items-center justify-center">
                      <v-icon color="yellow-darken-3" size="12">mdi-star</v-icon>
                    </div>
                  </div>

                  <!-- Player Info -->
                  <div class="flex-1 min-w-0">
                    <h4 class="text-xl font-bold text-gray-800 truncate">{{ entry.username }}</h4>
                    <div class="flex items-center gap-4 mt-2">
                      <div class="flex items-center gap-2">
                        <div class="w-6 h-6 bg-yellow-100 rounded-full flex items-center justify-center">
                          <v-icon color="yellow-darken-2" size="12">mdi-diamond-stone</v-icon>
                        </div>
                        <span class="text-sm font-bold text-yellow-700">{{ entry.ducksHiddenByRarity?.LEGENDARY || 0 }}</span>
                      </div>
                      <div class="flex items-center gap-2">
                        <div class="w-6 h-6 bg-purple-100 rounded-full flex items-center justify-center">
                          <v-icon color="purple-darken-2" size="12">mdi-star</v-icon>
                        </div>
                        <span class="text-sm font-bold text-purple-700">{{ entry.ducksHiddenByRarity?.RARE || 0 }}</span>
                      </div>
                      <div class="flex items-center gap-2">
                        <div class="w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center">
                          <v-icon color="blue-darken-2" size="12">mdi-circle</v-icon>
                        </div>
                        <span class="text-sm font-bold text-blue-700">{{ entry.ducksHiddenByRarity?.COMMON || 0 }}</span>
                      </div>
                    </div>
                  </div>

                  <!-- Score -->
                  <div class="text-right">
                    <div class="text-3xl font-black text-orange-600">{{ calculateHiddenScore(entry) }}</div>
                    <div class="text-xs text-gray-500 uppercase tracking-wide font-semibold">total points</div>
                  </div>
                </div>
              </div>

              <div v-if="displayTopHiders.length === 0" class="py-16 text-center">
                <div class="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <v-icon size="40" color="gray">mdi-eye-off</v-icon>
                </div>
                <h4 class="text-xl font-bold text-gray-800 mb-2">No Hiders Yet</h4>
                <p class="text-gray-600">Be the first to hide a duck!</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Call to Action -->
      <div v-if="leaderboard" class="text-center mt-16">
        <div class="bg-gradient-to-r from-blue-600 to-purple-600 rounded-3xl p-8 text-white shadow-2xl">
          <h3 class="text-3xl font-black mb-4">Ready to Join the Legends?</h3>
          <p class="text-xl text-blue-100 mb-8 max-w-2xl mx-auto">
            Every duck you find or hide earns you points. Legendary ducks are worth 3 points, rare ducks 2 points, and common ducks 1 point.
          </p>
          <div class="flex justify-center">
            <button 
              @click="emit('scrollToSection', 'pricing')"
              class="group inline-flex items-center px-8 py-4 bg-white text-blue-600 rounded-2xl font-bold text-lg hover:bg-gray-50 transition-all duration-300 shadow-lg hover:shadow-xl transform hover:scale-105"
            >
              <div class="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center mr-3 group-hover:bg-blue-200 transition-colors">
                <v-icon size="20" color="blue">mdi-plus</v-icon>
              </div>
              Hide Your Duck
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Mobile Fullscreen Leaderboard -->
    <v-dialog
      v-model="mobileDialog"
      fullscreen
      scrollable
      transition="dialog-bottom-transition"
      persistent
      scroll-strategy="block"
      retain-focus
      @click:outside="refocusLeaderboardDialog"
    >
      <div class="h-full bg-white flex flex-col">
        <!-- Header -->
        <div class="flex items-center justify-between px-4 py-3 border-b">
          <div class="flex items-center gap-2">
            <v-icon color="primary">mdi-trophy</v-icon>
            <h3 class="text-lg font-bold">Leaderboard</h3>
          </div>
          <button ref="leaderboardCloseBtnRef" class="p-2 rounded-lg hover:bg-gray-100" @click="mobileDialog = false" aria-label="Close leaderboard">
            <v-icon>mdi-close</v-icon>
          </button>
        </div>

        <!-- Tabs -->
        <div class="px-4 pt-3">
          <div class="inline-flex rounded-xl bg-gray-100 p-1">
            <button :class="tabBtnClass('finders')" @click="activeMobileTab = 'finders'">
              <v-icon size="18" class="mr-1">mdi-duck</v-icon>
              Finders
            </button>
            <button :class="tabBtnClass('hiders')" @click="activeMobileTab = 'hiders'">
              <v-icon size="18" class="mr-1">mdi-eye-off</v-icon>
              Hiders
            </button>
          </div>
        </div>

        <!-- Content -->
        <div class="flex-1 overflow-auto">
          <!-- Finders -->
          <div v-if="activeMobileTab === 'finders'" class="divide-y">
            <div
              v-for="(entry, index) in fullTopFinders"
              :key="`m-f-${entry.username}`"
              class="px-4 py-3"
            >
              <div class="flex items-center justify-between gap-3">
                <div class="flex items-center gap-3 min-w-0">
                  <div
                    class="w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold"
                    :class="[
                      index === 0 ? 'bg-yellow-400 text-yellow-900' :
                      index === 1 ? 'bg-gray-400 text-gray-900' :
                      index === 2 ? 'bg-orange-400 text-orange-900' :
                      'bg-blue-400 text-blue-900'
                    ]"
                  >
                    {{ index + 1 }}
                  </div>
                  <p class="font-semibold text-gray-800 truncate">{{ entry.username }}</p>
                </div>
                <div class="text-right">
                  <p class="text-sm font-bold text-emerald-700">{{ calculateFoundScore(entry) }}</p>
                  <div class="mt-1 flex items-center gap-3 tabular-nums text-xs justify-end">
                    <span class="inline-flex items-center gap-1 text-yellow-700">
                      <v-icon size="16">mdi-diamond-stone</v-icon>
                      <span class="w-6 text-right">{{ entry.ducksFoundByRarity?.LEGENDARY || 0 }}</span>
                    </span>
                    <span class="inline-flex items-center gap-1 text-purple-700">
                      <v-icon size="16">mdi-star</v-icon>
                      <span class="w-6 text-right">{{ entry.ducksFoundByRarity?.RARE || 0 }}</span>
                    </span>
                    <span class="inline-flex items-center gap-1 text-blue-700">
                      <v-icon size="16">mdi-circle</v-icon>
                      <span class="w-6 text-right">{{ entry.ducksFoundByRarity?.COMMON || 0 }}</span>
                    </span>
                  </div>
                </div>
              </div>
            </div>

            <div v-if="fullTopFinders.length === 0" class="py-10 text-center text-gray-500">
              <v-icon size="48" class="mb-2 opacity-50">mdi-duck</v-icon>
              <p>No duck finders yet. Be the first!</p>
            </div>
          </div>

          <!-- Hiders -->
          <div v-else class="divide-y">
            <div
              v-for="(entry, index) in fullTopHiders"
              :key="`m-h-${entry.username}`"
              class="px-4 py-3"
            >
              <div class="flex items-center justify-between gap-3">
                <div class="flex items-center gap-3 min-w-0">
                  <div
                    class="w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold"
                    :class="[
                      index === 0 ? 'bg-yellow-400 text-yellow-900' :
                      index === 1 ? 'bg-gray-400 text-gray-900' :
                      index === 2 ? 'bg-orange-400 text-orange-900' :
                      'bg-blue-400 text-blue-900'
                    ]"
                  >
                    {{ index + 1 }}
                  </div>
                  <p class="font-bold text-gray-800 truncate">{{ entry.username }}</p>
                </div>
                <div class="text-right">
                  <p class="text-sm font-extrabold text-orange-700">{{ calculateHiddenScore(entry) }}</p>
                  <div class="mt-1 flex items-center gap-3 tabular-nums text-xs justify-end">
                    <span class="inline-flex items-center gap-1 text-yellow-700">
                      <v-icon size="16">mdi-diamond-stone</v-icon>
                      <span class="w-6 text-right">{{ entry.ducksHiddenByRarity?.LEGENDARY || 0 }}</span>
                    </span>
                    <span class="inline-flex items-center gap-1 text-purple-700">
                      <v-icon size="16">mdi-star</v-icon>
                      <span class="w-6 text-right">{{ entry.ducksHiddenByRarity?.RARE || 0 }}</span>
                    </span>
                    <span class="inline-flex items-center gap-1 text-blue-700">
                      <v-icon size="16">mdi-circle</v-icon>
                      <span class="w-6 text-right">{{ entry.ducksHiddenByRarity?.COMMON || 0 }}</span>
                    </span>
                  </div>
                </div>
              </div>
            </div>

            <div v-if="fullTopHiders.length === 0" class="py-10 text-center text-gray-500">
              <v-icon size="48" class="mb-2 opacity-50">mdi-eye-off</v-icon>
              <p>No duck hiders yet. Be the first!</p>
            </div>
          </div>
        </div>
      </div>
    </v-dialog>
  </section>
</template>

<script setup lang="ts">
import { ref, onMounted, computed, watch, nextTick } from 'vue'
import axios from 'axios'
import { Constant } from '@/config/constants'
import type { Leaderboard } from '@/interfaces/leaderboard'

// Props
defineProps<{
  reducedMotion?: boolean
}>()

// Emits
const emit = defineEmits<{
  scrollToSection: [section: string]
}>()

// State
const leaderboard = ref<Leaderboard | null>(null)
const loading = ref(false)
const error = ref(false)
const isExpanded = ref(false)
const mobileDialog = ref(false)
const activeMobileTab = ref<'finders' | 'hiders'>('finders')
const leaderboardCloseBtnRef = ref<HTMLElement | null>(null)
const refocusLeaderboardDialog = () => {
  leaderboardCloseBtnRef.value?.focus?.()
}
watch(mobileDialog, async (val) => {
  if (val) {
    await nextTick()
    leaderboardCloseBtnRef.value?.focus?.()
  }
})

// Computed properties for display data
const displayTopFinders = computed(() => {
  if (!leaderboard.value) return []
  // Sort by calculated found score (Legendary=3, Rare=2, Common=1)
  const sortedFinders = [...leaderboard.value.topFinders].sort((a, b) => {
    const scoreA = calculateFoundScore(a)
    const scoreB = calculateFoundScore(b)
    return scoreB - scoreA
  })
  return isExpanded.value ? sortedFinders : sortedFinders.slice(0, 3)
})

const displayTopHiders = computed(() => {
  if (!leaderboard.value) return []
  // Sort by calculated hidden score (Legendary=3, Rare=2, Common=1)
  const sortedHiders = [...leaderboard.value.topHiders].sort((a, b) => {
    const scoreA = calculateHiddenScore(a)
    const scoreB = calculateHiddenScore(b)
    return scoreB - scoreA
  })
  return isExpanded.value ? sortedHiders : sortedHiders.slice(0, 3)
})

// Add computed properties for full lists (mobile dialog)
const fullTopFinders = computed(() => {
  if (!leaderboard.value) return []
  const sorted = [...leaderboard.value.topFinders].sort((a, b) => {
    const scoreA = calculateFoundScore(a)
    const scoreB = calculateFoundScore(b)
    return scoreB - scoreA
  })
  return sorted
})

const fullTopHiders = computed(() => {
  if (!leaderboard.value) return []
  const sorted = [...leaderboard.value.topHiders].sort((a, b) => {
    const scoreA = calculateHiddenScore(a)
    const scoreB = calculateHiddenScore(b)
    return scoreB - scoreA
  })
  return sorted
})

// Helper functions to calculate scores
const calculateFoundScore = (entry: any) => {
  if (!entry.ducksFoundByRarity) return entry.ducksFound
  return (entry.ducksFoundByRarity.LEGENDARY * 3) + 
         (entry.ducksFoundByRarity.RARE * 2) + 
         (entry.ducksFoundByRarity.COMMON * 1)
}

const calculateHiddenScore = (entry: any) => {
  if (!entry.ducksHiddenByRarity) return entry.ducksHidden
  return (entry.ducksHiddenByRarity.LEGENDARY * 3) + 
         (entry.ducksHiddenByRarity.RARE * 2) + 
         (entry.ducksHiddenByRarity.COMMON * 1)
}

// Methods
const toggleExpanded = () => {
  if (isMobile()) {
    activeMobileTab.value = 'finders'
    mobileDialog.value = true
    return
  }
  isExpanded.value = !isExpanded.value
}

const fetchLeaderboard = async () => {
  try {
    loading.value = true
    error.value = false
    
    const response = await axios.get(`${Constant.API_URL}${Constant.LEADERBOARD}`)
    leaderboard.value = response.data
  } catch (err) {
    console.error('Failed to fetch leaderboard:', err)
    error.value = true
  } finally {
    loading.value = false
  }
}

// Lifecycle
onMounted(() => {
  fetchLeaderboard()
})

// Helper functions
const isMobile = () => typeof window !== 'undefined' && window.matchMedia('(max-width: 639px)').matches
const tabBtnClass = (tab: 'finders' | 'hiders') => [
  'px-3 py-2 rounded-lg text-sm font-medium transition-colors',
  activeMobileTab.value === tab ? 'bg-white shadow text-gray-900' : 'text-gray-600 hover:text-gray-900'
]
</script>

<style scoped>
.animate-float-subtle {
  animation: float 4s ease-in-out infinite;
}

@keyframes float {
  0%, 100% { 
    transform: translateY(0px) rotate(0deg); 
  }
  50% { 
    transform: translateY(-8px) rotate(2deg); 
  }
}

.tabular-nums {
  font-variant-numeric: tabular-nums;
}

/* Enhanced hover animations */
.group:hover .group-hover\:scale-110 {
  transform: scale(1.1);
}

.group:hover .group-hover\:bg-blue-200 {
  background-color: rgb(219 234 254);
}

.group:hover .group-hover\:bg-white\/30 {
  background-color: rgba(255, 255, 255, 0.3);
}

/* Podium animation */
@keyframes podium-rise {
  from {
    transform: translateY(50px);
    opacity: 0;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
}

.animate-podium-rise {
  animation: podium-rise 0.8s ease-out forwards;
}

/* Custom pulse for champions */
@keyframes champion-pulse {
  0%, 100% {
    transform: scale(1);
    box-shadow: 0 0 0 0 rgba(251, 191, 36, 0.7);
  }
  50% {
    transform: scale(1.05);
    box-shadow: 0 0 0 10px rgba(251, 191, 36, 0);
  }
}

.animate-champion-pulse {
  animation: champion-pulse 2s infinite;
}

/* Responsive design improvements */
@media (max-width: 640px) {
  .animate-float-subtle {
    animation: float-mobile 3s ease-in-out infinite;
  }
  
  @keyframes float-mobile {
    0%, 100% { 
      transform: translateY(0px); 
    }
    50% { 
      transform: translateY(-4px); 
    }
  }
}
</style>